<template>
    <modal size="lg" @close="reset(); $emit('close')" ref="modal" :is-closeable="isCloseable" id="editPositionModal" :autoshow="true" v-if="dataLoaded">
        <template v-slot:title>
            <h3 class="modal-title">Edit Position {{ editModePretty }}</h3>
        </template>

        <slot>
            <div class="mb-2">
                <div class="p-1 unpad-x bg-150 rounded">
                    <div><span class="text-muted">Company: </span>{{ position.company.name }}</div>
                    <div><span class="text-muted">Position: </span>{{ position.terminal_value }}</div>
                </div>
            </div>

            <form-errors :errors="formErrors"/>

            <div v-if="editMode == 'info'" class="row">
                <div class="col-12">
                    <div class="card">
                        <div class="card-body">
                            <form>
                                <div class="form-row">
                                    <div class="col-4">
                                        <form-input label="POS Value" v-model="formData.pos_value" :errors="errors.pos_value" type="text" :maxlength="50"/>
                                    </div>
                                    <div class="col-4">
                                        <form-input label="POS Value 2" v-model="formData.pos_value_2" :errors="errors.pos_value_2" type="text" :maxlength="50" :helper-text="'Sandbox/migration column for TC+ (see Admin Tools).'"/>
                                    </div>
                                    <div class="col-4">
                                        <form-input label="SOC Code" v-model="formData.soc_code" :errors="errors.soc_code" type="text" :maxlength="20"/>
                                    </div>
                                </div>

                                <template v-for="i in 5" :key="i">
                                    <form-input v-if="i == client.location_org_level" label="Location" type="select" :options="locationOptions" v-model="formData.work_location" :errors="errors.work_location"/>
                                    <form-input v-else-if="orgOptionsByLevel[i] && orgOptionsByLevel[i].length" :label="client[`org_level_${i}_label`]" type="select" :options="orgOptionsByLevel[i]" v-model="formData[`org_level_${i}_value`]" :errors="errors[`org_level_${i}_value`]"/>
                                </template>

                                <form-input label="Pay Group" v-model="formData.paygroup" :errors="errors.paygroup" type="select" :options="paygroupOptions"/>

                                <form-input label="Delivery Address" v-model="formData.delivery_address" :errors="errors.delivery_address" type="select" :options="deliveryOptions" :noblank="true"/>

                                <div class="form-row">
                                    <div class="col-6">
                                        <form-input v-model="formData.bank_account_net_pay" :errors="errors.bank_account_net_pay" label="Net Pay Bank Account" type="select" :options="bankAccountOptions" :noblank="true" />
                                    </div>

                                    <div class="col-6">
                                        <form-input v-model="formData.bank_account_taxes" :errors="errors.bank_account_taxes" label="Taxes Bank Account" type="select" :options="bankAccountOptions" :noblank="true" />
                                    </div>
                                </div>

                                <form-input v-model="formData.is_regularly_tipped" type="checkbox" :errors="errors.is_regularly_tipped" label="Regularly Tipped Position" helper-text="Employer can claim tip credit for this employee"></form-input>
                            </form>
                        </div>
                    </div>
                </div>
            </div>

            <div v-if="editMode == 'earnings'" >
                <div v-if="step == 'earnings'">
                    <p>Select the earnings this position can use.</p>
                    <earning-selector v-if="selectableEarningTypes" v-model="formData.company_earning_types" :selectableEarnings="selectableEarningTypes" :lockedEarningIds="allPositionsEarningTypeIds"/>
                </div>

                <div v-if="step == 'default-earnings'">
                    <p>Select the earnings this position will use by default.</p>
                    <earning-selector v-if="selectableEarningTypes" v-model="formData.default_company_earning_types" :selectableEarnings="selectedAvailableEarningTypes" :lockedEarningIds="allPositionsDefaultEarningTypeIds"/>
                </div>
            </div>
            <div v-if="editMode == 'deductions'">
                <p class="mb-0">Which deductions are available for this position?</p>
                <deduction-selector v-if="selectableDeductionTypes" v-model="formData.deduction_types" :selectableDeductions="selectableDeductionTypes"/>
            </div>
            <div v-if="editMode == 'reimbursements'">
                <p class="mb-0">Which reimbursements are available for this position?</p>
                <reimbursement-selector v-if="selectableReimbursementTypes" v-model="formData.reimbursement_types" :selectableDeductions="selectableReimbursementTypes"/>
            </div>

        </slot>

        <template v-slot:footer>
            <button-row stacked>
                <button v-if="steps?.length && step != steps[0]" type="button" class="btn btn-outline-primary" @click.prevent="step = steps[steps.indexOf(step)-1]">
                    Back
                </button>
                <button v-else type="button" class="btn btn-outline-primary" @click.prevent="close()">
                    Cancel
                </button>
                <button v-if="steps?.length && step != steps[steps.length-1]" type="button" class="btn btn-primary" @click.prevent="step = steps[steps.indexOf(step)+1]">
                    Continue
                </button>
                <button v-else type="button" class="btn btn-primary" @click.prevent="onSubmit()">
                    Save
                </button>
            </button-row>
        </template>
    </modal>
</template>

<script>
import FormMixin from '@/mixins/Form'
import ModalMixin from '@/mixins/ModalMixin'
import EarningSelector from '@/pages/client/Settings/components/EarningSelector'
import DeductionSelector from '@/pages/client/Settings/components/DeductionSelector'
import ReimbursementSelector from '@/pages/client/Settings/components/ReimbursementSelector'

export default {
    props: ['client', 'position', 'editMode'],
    mixins: [FormMixin, ModalMixin],
    components: {EarningSelector, DeductionSelector, ReimbursementSelector},
    data() {
        let data = {
            formData: this.makeFormData(),
            bankAccounts: null,
            orgs: null,
            locations: null,
            paygroups: null,
            deliveries: null,
            selectableEarningTypes: null,
            selectableDeductionTypes: null,
            selectableReimbursementTypes: null,
            step: '',
            steps: []
        }
        if (this.editMode == 'earnings') {
            data.steps = ['earnings', 'default-earnings']
            data.step = data.steps[0]
        }
        return data
    },
    mounted() {
        this.getData()
    },
    computed: {
        dataLoaded() {
            return this.bankAccounts && this.orgs && this.locations && this.paygroups && this.deliveries && this.selectableEarningTypes && this.selectableDeductionTypes && this.selectableReimbursementTypes
        },
        editModePretty() {
            if (this.editMode == 'earnings' && this.step == 'default-earnings') {
                return 'Default Earnings'
            }
            return {
                'earnings': 'Earnings',
                'deductions': 'Deductions',
                'reimbursements': 'Reimbursements',
                'info': 'Information',
                '': '',
            }[this.editMode]
        },
        orgOptionsByLevel() {
            if (!this.dataLoaded) {return {}}

            const result = {}
            for (let i = 1; i <= 5; i++) {
                result[i] = []
                this.orgs.filter(o => o.org_level == i).forEach(o => {
                    result[i].push({text: o.org_level_description, value: o.org_level_description})
                })

                result[i].sort((a, b) => {
                    return (a.text > b.text) ? 1 : -1
                })

            }

            return result
        },
        locationOptions() {
            if (!this.dataLoaded) {return []}

            const result = []
            this.locations.forEach(l => {
                result.push({text: l.name, value: l.id})
            })

            result.sort((a, b) => {
                return (a.text > b.text) ? 1 : -1
            })

            return result
        },
        paygroupOptions() {
            if (!this.dataLoaded) {return []}

            const result = []
            let paygroups = this.paygroups
            if (this.formData.work_location) {
                const location = this.locations.find(l => l.id == this.formData.work_location)
                paygroups = this.paygroups.filter(p => p.company_id == location.company_id)
            }
            paygroups.forEach(l => {
                result.push({text: l.name, value: l.id})
            })

            result.sort((a, b) => {
                return (a.text > b.text) ? 1 : -1
            })

            return result
        },
        bankAccountOptions() {
            if (!this.dataLoaded) {return []}

            const result = [
                {text: '--Default--', value: ''}
            ]

            this.bankAccounts.forEach(b => {
                result.push({text: b.name, value: b.id})
            })

            result.sort((a, b) => {
                if (!a.value) {return -1}
                if (!b.value) {return 1}
                return (a.text > b.text) ? 1 : -1
            })

            return result
        },
        deliveryOptions() {
            if (!this.dataLoaded) {return []}

            const result = [
                {text: 'Use Default', value: 'default'}
            ]

            this.deliveries.forEach(d => {
                result.push({text: d.name, value: d.id})
            })

            result.sort((a, b) => {
                if (a.value == 'default') {return -1}
                if (b.value == 'default') {return 1}
                return (a.text > b.text) ? 1 : -1
            })

            return result
        },
        // All-positions earnings apply to every position without being assigned, so they
        // show locked here and are changed on the Earnings settings screen
        allPositionsEarningTypeIds() {
            return this.selectableEarningTypes.filter(e => e.default_for_all_positions).map(e => e.id)
        },
        allPositionsDefaultEarningTypeIds() {
            return this.selectableEarningTypes.filter(e => e.default_for_all_positions && e.always_show_on_paygrid).map(e => e.id)
        },
        selectedAvailableEarningTypes() {
            return this.selectableEarningTypes.filter(e => {
                return this.formData.company_earning_types.includes(e.id) || this.allPositionsDefaultEarningTypeIds.includes(e.id) || e.slug == 'regular'
            })
        }
    },
    methods: {
        makeFormData() {
            const data = {
                company_earning_types: [],
                default_company_earning_types: [],
                deduction_types: [],
                reimbursement_types: [],
                soc_code: this.position.soc_code,
                pos_value: this.position.pos_value,
                pos_value_2: this.position.pos_value_2,
                org_level_1_value: this.position.org_level_1_value,
                org_level_2_value: this.position.org_level_2_value,
                org_level_3_value: this.position.org_level_3_value,
                org_level_4_value: this.position.org_level_4_value,
                org_level_5_value: this.position.org_level_5_value,
                work_location: this.position.work_location_id,
                paygroup: this.position.paygroup.id,
                delivery_address: this.position.delivery_address_id || 'default',
                bank_account_net_pay: this.position.bank_account_net_pay_id,
                bank_account_taxes: this.position.bank_account_taxes_id,
                is_regularly_tipped: this.position.is_regularly_tipped,
            }

            // Only active per-position earnings can be assigned here; rels to any others are
            // left alone by the save
            this.position.earning_type_rels.filter(rel => {
                return rel.company_earning_type.is_active && !rel.company_earning_type.default_for_all_positions
            }).forEach(rel => {
                data.company_earning_types.push(rel.company_earning_type.id)
                if (rel.is_default) {
                    data.default_company_earning_types.push(rel.company_earning_type.id)
                }
            })

            this.position.deduction_type_rels.forEach(rel => {
                data.deduction_types.push(rel.company_deduction_type.deduction_type.id)
            })

            this.position.reimbursement_type_rels.forEach(rel => {
                data.reimbursement_types.push(rel.company_reimbursement_type.reimbursement_type.id)
            })

            return data
        },
        getOrgs() {
            return this.$api.get(`/clients/${this.client.id}/settings/company-orgs`).then(resp => {
                this.orgs = resp
            })
        },
        getLocations() {
            return this.$api.get(`/clients/${this.client.id}/settings/locations`).then(resp => {
                this.locations = resp
            })
        },
        getPaygroups() {
            return this.$api.get(`/clients/${this.client.id}/settings/paygroups`).then(resp => {
                this.paygroups = resp
            })
        },
        getBankAccounts() {
            return this.$api.get(`/clients/${this.client.id}/settings/bank-accounts`).then(resp => {
                this.bankAccounts = resp.filter(b => b.company_id == this.position.company_id)
            })
        },
        getDeliveries() {
            return this.$api.get(`/clients/${this.client.id}/settings/delivery-addresses`).then(resp => {
                this.deliveries = resp.filter(d => d.company_id == this.position.company_id)
            })
        },
        getSelectableEarningTypes() {
            return this.$api.get(`/clients/${this.client.id}/settings/earning-types`).then(resp => {
                this.selectableEarningTypes = resp.filter(e => e.company_id == this.position.company_id && e.is_active)
            })
        },
        getSelectableDeductionTypes() {
            this.$api.get(`/admin/tax-engine/deduction-types?categories=other-benefits,misc`).then(resp => {
                this.selectableDeductionTypes = resp
            })
        },
        getSelectableReimbursementTypes() {
            this.$api.get(`/admin/tax-engine/reimbursement-types`).then(resp => {
                this.selectableReimbursementTypes = resp
            })
        },
        getData() {
            this.$store.dispatch('START_LOADING')
            const promises = [
                this.getOrgs(),
                this.getLocations(),
                this.getPaygroups(),
                this.getBankAccounts(),
                this.getDeliveries(),
                this.getSelectableDeductionTypes(),
                this.getSelectableEarningTypes(),
                this.getSelectableReimbursementTypes(),
            ]

            Promise.all(promises).then(() => {
                this.$store.dispatch('STOP_LOADING')
            }).catch(err => {
                this.$store.dispatch('STOP_LOADING')
                this.$bus.showError('Could not load form. Please refresh the page and try again.')
            })
        },
        onSubmit() {
            this.$store.dispatch('START_LOADING')

            const URLS = {
                earnings: `/clients/${this.client.id}/settings/positions/${this.position.id}/earning-types`,
                deductions: `/clients/${this.client.id}/settings/positions/${this.position.id}/deduction-types`,
                reimbursements: `/clients/${this.client.id}/settings/positions/${this.position.id}/reimbursement-types`,
                info: `/clients/${this.client.id}/settings/positions/${this.position.id}`,
            }
            const url = URLS[this.editMode]
            if (!url) {return}

            return this.$api.patch(url, this.getFormData()).then(resp => {
                this.$store.dispatch('STOP_LOADING')
                this.$emit('updated', resp)
                this.close()
            }).catch(errors => {
                this.$store.dispatch('STOP_LOADING')
                this.errors = errors
                this.formErrors = errors.__all__
                this.onError()
            })
        }
    }
}
</script>
